import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, Lock } from "lucide-react";

export default function LeadCapture({ onSubmit, onBack }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });

  const valid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim().length >= 7;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handle = (e) => {
    e.preventDefault();
    if (valid) onSubmit(form);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-lg bg-white border border-slate-200 rounded-md p-8 md:p-10"
      >
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Almost there</div>
        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-bold text-slate-900">
          Where should we send your readiness report?
        </h2>
        <p className="mt-3 text-slate-600">
          Your AI-graded score and personalized action plan are ready. Tell us who's getting it.
        </p>

        <form onSubmit={handle} className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" data-testid="lead-name" value={form.name} onChange={set("name")} placeholder="Jordan Miller" className="h-12" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="lead-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="h-12" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" data-testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" className="h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Company <span className="text-slate-400">(optional)</span></Label>
              <Input id="company" data-testid="lead-company" value={form.company} onChange={set("company")} placeholder="Miller Trucking" className="h-12" />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onBack} data-testid="lead-back" className="text-slate-500">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button
              type="submit"
              data-testid="lead-submit"
              disabled={!valid}
              className="flex-1 h-12 rounded-full bg-brand hover:bg-brand-hover text-white font-bold disabled:opacity-40"
            >
              Reveal my score <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <p className="flex items-center gap-1.5 text-xs text-slate-400 pt-1">
            <Lock className="w-3 h-3" /> We never share your info. Unsubscribe anytime.
          </p>
        </form>
      </motion.div>
    </div>
  );
}
